import React from 'react';

import type { GameLogos } from '../../ad-subjects';
import type { PlayableVersion } from '../../playable-api';
import { POSTER_FRAME, buildPlayablePosterDoc, posterScale } from '../../playable-poster';
import type { PlayableRuntime } from '../../playable-preview';
import { revealOnLoad } from '../../reveal-media';
import { PreviewFrame } from '../PlayableComposer/PreviewFrame';
import * as S from './GamePoster.styles';

export interface GamePosterProps {
  /** The version's background plate: the poster until the game has loaded, and the whole poster when there is no
   *  game to draw. */
  plateUrl: string;
  /** The version the card shows (see `posterVersion`). */
  version: PlayableVersion;
  /** The built runtime from the template registry; null while it loads or where previews are unavailable. */
  runtime: PlayableRuntime | null;
  /** Translated name of the game frame (the frame is inert and hidden from assistive tech: the card names itself). */
  title: string;
  /** The brand's mini-game logos as they are now (the game is drawn with them, see `withGameLogos`). */
  logos?: GameLogos;
}

/** How far ahead of the viewport a card starts loading its game (the frame is ready as it scrolls in). */
const IN_VIEW_MARGIN = '200px 0px';

interface Box {
  w: number;
  h: number;
}

/** Marks the game layer inert (no focus, no assistive tech inside the frame); React 18 has no `inert` prop. */
const inertRef = (el: HTMLDivElement | null): void => {
  el?.setAttribute('inert', '');
};

/**
 * A Mini-games library card's poster: the game at rest (the runtime's resting scene over the version's art, static,
 * see `playable-poster`) over its background plate. The frame mounts only once the card scrolls into view and stays
 * mounted after; until it has loaded, and whenever there is no game to draw (the runtime still loading or not built,
 * no bundle for the template), the plate shows as before. Never interactive: the card's click opens the editor.
 */
export const GamePoster = ({ plateUrl, version, runtime, title, logos }: GamePosterProps): React.ReactElement => {
  const boxRef = React.useRef<HTMLDivElement>(null);
  const [inView, setInView] = React.useState(false);
  const [box, setBox] = React.useState<Box | null>(null);
  const [loaded, setLoaded] = React.useState(false);

  // Syncs with the viewport: the first time the card is (nearly) on screen, its game may mount. Without an
  // IntersectionObserver (an old engine) it mounts at once rather than never.
  React.useEffect(() => {
    if (inView || !runtime) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }
    const el = boxRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        setInView(true);
      },
      { rootMargin: IN_VIEW_MARGIN },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [inView, runtime]);

  const html = React.useMemo(
    () => (inView && runtime ? buildPlayablePosterDoc(runtime, version, logos) : null),
    [inView, runtime, version, logos],
  );

  // Syncs with the layout: the poster's size, which the game frame is scaled to (never re-laid out).
  React.useEffect(() => {
    const el = boxRef.current;
    if (!el || !html) return undefined;
    const read = (): void =>
      setBox((cur) =>
        cur && cur.w === el.clientWidth && cur.h === el.clientHeight ? cur : { w: el.clientWidth, h: el.clientHeight },
      );
    read();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(read);
    observer.observe(el);
    return () => observer.disconnect();
  }, [html]);

  const scale = box ? posterScale(box.w, box.h) : 0;
  return (
    <S.GamePosterBox ref={boxRef}>
      <S.PosterImg key={plateUrl} {...revealOnLoad} src={plateUrl} alt="" />
      {html && scale > 0 ? (
        <S.GameLayer ref={inertRef} aria-hidden="true" $shown={loaded}>
          <PreviewFrame
            html={html}
            w={POSTER_FRAME.w}
            h={POSTER_FRAME.h}
            scale={scale}
            title={title}
            onLoad={() => setLoaded(true)}
          />
        </S.GameLayer>
      ) : null}
    </S.GamePosterBox>
  );
};

export default GamePoster;
